import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Send, 
  Phone, 
  MoreVertical,
  Search,
  CheckCheck,
  Clock,
} from 'lucide-react';

const conversations = [
  { id: 1, name: 'John Doe', lastMessage: 'Is the rate still available?', time: '2 min ago', unread: 2, online: true },
  { id: 2, name: 'Sarah Smith', lastMessage: 'Thanks for the quick exchange!', time: '1 hour ago', unread: 0, online: false },
  { id: 3, name: 'Mike Johnson', lastMessage: 'Can we meet at Lekki?', time: '2 hours ago', unread: 1, online: true },
  { id: 4, name: 'Emma Wilson', lastMessage: 'I will be there in 10 minutes', time: '3 hours ago', unread: 0, online: false },
];

const messages = [
  { id: 1, sender: 'them', text: 'Hi, I saw your listing for USD', time: '10:30 AM' },
  { id: 2, sender: 'me', text: 'Hello! Yes, the rate is still available', time: '10:32 AM' },
  { id: 3, sender: 'them', text: 'Great! I need $2,000. Is that okay?', time: '10:33 AM' },
  { id: 4, sender: 'me', text: 'Yes, that works. When would you like to meet?', time: '10:35 AM' },
  { id: 5, sender: 'them', text: 'Is the rate still available?', time: '10:40 AM' },
];

export default function VendorMessages() {
  return (
    
      <div className="h-[calc(100vh-8rem)]">
        <Card className="h-full">
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className="border-r">
              <CardHeader>
                <CardTitle>Messages</CardTitle>
                <CardDescription>Chat with your customers</CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search conversations..." className="pl-10" />
                </div>
              </CardHeader>
              <ScrollArea className="h-[calc(100%-8rem)]">
                <div className="space-y-1 p-4">
                  {conversations.map((conv) => (
                    <div 
                      key={conv.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors ${conv.unread > 0 ? 'bg-violet-500/5' : ''}`}
                    >
                      <div className="relative">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-violet-500/20 text-violet-500">
                            {conv.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {conv.online && (
                          <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-background" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">{conv.name}</p>
                          <span className="text-xs text-muted-foreground">{conv.time}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground truncate">{conv.lastMessage}</p>
                          {conv.unread > 0 && (
                            <Badge className="h-5 w-5 p-0 flex items-center justify-center bg-violet-500">
                              {conv.unread}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className="md:col-span-2 flex flex-col h-full">
              {/* Chat Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-violet-500/20 text-violet-500">J</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">John Doe</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      Online
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon">
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                          msg.sender === 'me' 
                            ? 'bg-violet-500 text-foreground rounded-br-none' 
                            : 'bg-muted rounded-bl-none'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <p className={`text-xs mt-1 ${msg.sender === 'me' ? 'text-violet-200' : 'text-muted-foreground'}`}>
                          {msg.time}
                          {msg.sender === 'me' && <CheckCheck className="inline h-3 w-3 ml-1" />}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Input Area */}
              <div className="p-4 border-t">
                <div className="flex items-center gap-2">
                  <Input placeholder="Type a message..." className="flex-1" />
                  <Button className="bg-violet-500 hover:bg-violet-600">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    
  );
}
